import { $, $$, el, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm, formToObject } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { mountResourceList } from '../lib/resource-list.js';
import { mountRolePicker, type RoleOption } from '../lib/role-picker.js';
import { findSelectMenu } from '../lib/select-menu.js';

interface Question {
  label: string;
  style: string;
  placeholder: string;
  required: boolean;
  maxLength: number;
}

interface Slot {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

interface Panel {
  _id: string;
  name: string;
  channelId: string;
  categoryId: string;
  transcriptChannelId: string;
  staffRoleIds: string[];
  openRoleIds: string[];
  mentionStaffOnOpen: boolean;
  maxOpenPerUser: number;
  panel: Record<string, string>;
  welcome: Record<string, string>;
  claimEnabled: boolean;
  claimRestricts: boolean;
  questions: Question[];
  hours: { enabled: boolean; slots: Slot[]; closedMessage: string };
}

interface EditorPageData {
  panel: Panel;
  roles: RoleOption[];
  guildId: string;
  questionStyles: string[];
}

/** Discord's ceiling for a modal window, and therefore for this form. */
const MAX_QUESTIONS = 5;

/** The list: all it keeps is republishing. */
export function initTicketsListPage(): void {
  const list = document.querySelector('#panel-list');
  if (!list) return;

  const { guildId } = pageData<{ guildId: string }>();

  mountResourceList({
    endpoint: 'tickets',
    prefix: 'panel',
    guildId,
    listSelector: '#panel-list',
    successMessage: ct('panelSaved', 'Panel saved and published.'),
  });
}

/** Editing a ticket panel, on a page of its own. */
export function initTicketsEditorPage(): void {
  const form = $<HTMLFormElement>('[data-panel-form]');
  if (!form) return;

  const { panel, roles, guildId } = pageData<EditorPageData>();
  const staffScope = form.querySelector<HTMLElement>('[data-role-scope="staff"]') ?? form;
  const openScope = form.querySelector<HTMLElement>('[data-role-scope="open"]') ?? form;
  const rolePicker = mountRolePicker(staffScope, roles, panel.staffRoleIds ?? []);
  const openPicker = mountRolePicker(openScope, roles, panel.openRoleIds ?? []);

  // `fillForm` flattens `panel.title` and `welcome.description` by itself: no
  // need to enumerate every field.
  fillForm(form, panel as unknown as Record<string, unknown>);

  /*
   * The dropdowns, on the other hand, do not fill in that way.
   *
   * `upgradeSelects` replaced each `<select>` with a component that leaves a
   * hidden field behind it: `fillForm` does write the value there, but the
   * display keeps the first option. An existing panel therefore opened on the
   * wrong channel and the wrong category — the page looked empty when it was
   * full.
   */
  for (const [field, value] of [
    ['channelId', panel.channelId],
    ['categoryId', panel.categoryId],
    ['transcriptChannelId', panel.transcriptChannelId],
    ['panel.buttonStyle', panel.panel.buttonStyle],
  ] as const) {
    const component = findSelectMenu(form, field);
    if (component) component.value = value ? [value] : [];
  }

  const questions = mountQuestions(form, panel.questions ?? []);
  fillHours(form, panel.hours?.slots ?? []);

  mountResourceEditor({
    endpoint: 'tickets',
    listUrl: `/modules/tickets?guildId=${guildId}`,
    guildId,
    form,
    itemId: panel._id,

    serialize: () => {
      const values = formToObject(form);
      delete values.panelId;

      return {
        ...values,
        /*
         * Read from the element, not from `formToObject`.
         *
         * An unchecked box is absent from a form's data, and an absent field
         * means "leave it as it is" to the API — so switching this off would
         * never switch it off.
         */
        mentionStaffOnOpen: checked(form, 'mentionStaffOnOpen'),
        staffRoleIds: rolePicker?.value ?? [],
        openRoleIds: openPicker?.value ?? [],
        questions: questions(),
        hours: {
          enabled: checked(form, 'hours.enabled'),
          slots: readHours(form),
          closedMessage: text(form, 'hours.closedMessage'),
        },
      };
    },

    labels: {
      saved: ct('panelSaved', 'Panel saved and published.'),
      confirmDelete: ct(
        'confirmDeletePanel',
        'Delete this panel? The published message will be removed.',
      ),
      deleted: ct('panelDeleted', 'Panel deleted.'),
    },
  });
}

/* ------------------------------------------- formulaire d'ouverture */

/**
 * The list of questions, built in the browser.
 *
 * Returned as a reader rather than kept in a variable: the save serialises
 * whatever is on screen at that instant, and a copy taken at mount time would
 * have saved the questions the page opened with.
 */
function mountQuestions(form: HTMLFormElement, initial: Question[]): () => Question[] {
  const host = $<HTMLElement>('[data-questions]', form);
  const add = $<HTMLButtonElement>('[data-add-question]', form);
  if (!host || !add) return () => [];

  const draw = () => {
    // Five is Discord's ceiling for a modal, not a product choice: past it the
    // whole modal is refused, and the button would open nothing at all.
    add.hidden = host.children.length >= MAX_QUESTIONS;
  };

  const row = (question: Question) => {
    const line = el('div', { class: 'flex flex-wrap items-center gap-2' });

    const label = el('input', {
      class: 'input input-bordered input-sm flex-1',
      attrs: { type: 'text', maxlength: '45', 'data-question-label': '' },
    });
    label.value = question.label;

    const style = el('select', {
      class: 'select select-bordered select-sm w-40',
      attrs: { 'data-question-style': '' },
    });

    const styles: [string, string][] = [
      ['short', ct('questionStyleShort', 'One line')],
      ['paragraph', ct('questionStyleParagraph', 'Paragraph')],
    ];

    for (const [value, label] of styles) {
      const option = el('option', { text: label });
      option.value = value;
      style.append(option);
    }
    style.value = question.style;

    const required = el('input', {
      attrs: { type: 'checkbox', 'data-question-required': '', class: 'toggle toggle-sm' },
    });
    required.checked = question.required;

    const remove = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: ct('removeQuestion', 'Remove'),
      attrs: { type: 'button' },
    });
    remove.addEventListener('click', () => {
      line.remove();
      draw();
    });

    line.append(label, style, required, remove);
    return line;
  };

  for (const question of initial.slice(0, MAX_QUESTIONS)) host.append(row(question));
  draw();

  add.addEventListener('click', () => {
    host.append(
      row({ label: '', style: 'short', placeholder: '', required: true, maxLength: 300 }),
    );
    draw();
  });

  return () =>
    [...host.children].flatMap((line) => {
      const label = $<HTMLInputElement>('[data-question-label]', line)?.value.trim() ?? '';
      // A question with no wording would be a nameless field in the member's
      // modal; dropping it silently beats storing it and asking nothing.
      if (!label) return [];

      return [
        {
          label,
          style: $<HTMLSelectElement>('[data-question-style]', line)?.value ?? 'short',
          placeholder: '',
          required: $<HTMLInputElement>('[data-question-required]', line)?.checked ?? true,
          maxLength: 300,
        },
      ];
    });
}

/* ------------------------------------------------ horaires d'ouverture */

/** `HH:MM` from minutes since midnight, and back. */
function toClock(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function toMinutes(clock: string): number | null {
  const [hours, minutes] = clock.split(':').map(Number);
  if (
    hours === undefined ||
    minutes === undefined ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }
  return hours * 60 + minutes;
}

function fillHours(form: HTMLFormElement, slots: Slot[]): void {
  for (const slot of slots) {
    const row = $<HTMLElement>(`[data-day="${String(slot.weekday)}"]`, form);
    const start = $<HTMLInputElement>('[data-day-start]', row ?? form);
    const end = $<HTMLInputElement>('[data-day-end]', row ?? form);

    if (start) start.value = toClock(slot.startMinute);
    if (end) end.value = toClock(slot.endMinute);
  }
}

function readHours(form: HTMLFormElement): Slot[] {
  return $$<HTMLElement>('[data-day]', form).flatMap((row) => {
    const start = toMinutes($<HTMLInputElement>('[data-day-start]', row)?.value ?? '');
    const end = toMinutes($<HTMLInputElement>('[data-day-end]', row)?.value ?? '');

    // A day left blank is a closed day, not an error: seven rows are always on
    // screen, and most servers fill in two or three.
    if (start === null || end === null) return [];
    if (start === end) return [];

    return [{ weekday: Number(row.dataset.day ?? '0'), startMinute: start, endMinute: end }];
  });
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
